import { parsePercentToBps } from '@/utils/par-value';

/**
 * Reading ownership percentages out of whatever spreadsheet the association keeps — the
 * plain `account_number,pct_ownership` template, or a hand-built fee workbook like VHCC's
 * "2027 Fee Structure.xlsx" (headers on row 3, the account # under "Address", the share
 * under "Par Value" as an Excel percent, a totals row and footer notes).
 *
 * Nothing here trusts a header row position or an exact header name for the account #:
 * the account column is the one whose VALUES match this association's account numbers.
 * The percent column is found by its header, above the first matched row.
 */

const PERCENT_HEADER = /pct|percent|%|ownership|par[\s_-]*value|share/i;
/** A percent header that names the dues share outright, ranked above a generic pct / % / share. */
const OWNERSHIP_HEADER = /ownership|par[\s_-]*value/i;
/** A totals row labelled in the account column: "Total", "Grand total", "Subtotal", "Sum". */
const TOTAL_LABEL = /^(grand\s+)?(sub)?totals?\b|^sum\b/i;
/** A header naming the account # column outright. */
const ACCOUNT_HEADER = /acc(oun)?t|acct/i;
/** Headers that often hold it — VHCC's fee sheet heads its account codes "Address". */
const ACCOUNT_LIKE_HEADER = /unit|lot|address|#|number|\bno\b/i;

export interface ParValueSheetRow {
	accountNumber: string;
	parValueBps: number;
}

export interface ParValueSheetResult {
	rows: ParValueSheetRow[];
	/** Rows that name an account or carry a share but cannot be written, with why. */
	skipped: Array<{ accountNumber: string; reason: string }>;
	/** The header labels used, so the preview can say what was read. */
	accountColumn?: string;
	percentColumn?: string;
	/** True when bare numbers were read as fractions of 1 (0.01366 → 1.366%). */
	readAsFractions: boolean;
	/** Set when the file cannot be read at all; `rows` is then empty. */
	error?: string;
}

const norm = (s: string): string => s.trim().toLowerCase();
const bareNumber = (s: string): number | null => {
	if (s === '' || s.endsWith('%')) return null;
	const n = Number(s.replace(/,/g, ''));
	return Number.isFinite(n) ? n : null;
};

/**
 * Pull `{ accountNumber, parValueBps }` rows out of a sheet grid (see `sheetCells`).
 *
 * Bare numbers are percentages (4.23 = 4.23%) unless every one is at most 1 and together
 * they total ~1 — then the column is fractions of the whole (0.01366 = 1.366%), the way a
 * percent column reads once its formatting is lost. A row with neither a known account nor
 * a readable share (titles, totals, notes) is ignored rather than reported.
 */
export const extractParValueRows = (cells: string[][], accountNumbers: Iterable<string>): ParValueSheetResult => {
	const known = new Set([...accountNumbers].map(norm));
	const at = (r: number, c: number): string => (cells[r]?.[c] ?? '').trim();
	const width = cells.reduce((w, row) => Math.max(w, row.length), 0);
	const base: ParValueSheetResult = { rows: [], skipped: [], readAsFractions: false };

	const noAccounts = 'No column matches this association\'s account numbers — the file needs the account # (e.g. 2361SD) in its own column.';

	// ONE header row labels every column: the last row with a percent-like header above the
	// first row holding any known account number. Guessing a header per column let an unknown
	// account in the first data row pose as the Account column's header (Codex P1, #703).
	const firstDataRow = cells.findIndex((_row, r) => Array.from({ length: width }, (_, c) => at(r, c)).some((v) => known.has(norm(v))));
	if (firstDataRow < 0) return { ...base, error: noAccounts };
	let headerRow = -1;
	for (let r = firstDataRow - 1; r >= 0 && headerRow < 0; r--) {
		for (let c = 0; c < width; c++) if (PERCENT_HEADER.test(at(r, c))) headerRow = r;
	}
	if (headerRow < 0) {
		return {
			...base,
			error: 'No ownership percent column found — expected a header like "Par Value", "Ownership %" or "pct_ownership" above the first account row.',
		};
	}

	// Account candidates: columns not headed like a percent, holding known accounts below the header.
	const candidates: Array<{ col: number; matches: number; header: string }> = [];
	for (let c = 0; c < width; c++) {
		const header = at(headerRow, c);
		if (PERCENT_HEADER.test(header)) continue;
		let matches = 0;
		for (let r = headerRow + 1; r < cells.length; r++) if (known.has(norm(at(r, c)))) matches++;
		if (matches > 0) candidates.push({ col: c, matches, header });
	}
	if (candidates.length === 0) return { ...base, error: noAccounts };
	// Plain numeric account numbers also match a row-number column. A header that names the
	// account # outranks a bare count, and a tie that remains is refused: guessing writes every
	// share onto another unit (Codex P1, #703).
	// "account_number" beats "lot_number" in the customer template, before any count.
	const pool = [ACCOUNT_HEADER, ACCOUNT_LIKE_HEADER]
		.map((re) => candidates.filter((k) => re.test(k.header)))
		.find((tier) => tier.length > 0) ?? candidates;
	const bestMatches = Math.max(...pool.map((k) => k.matches));
	const best = pool.filter((k) => k.matches === bestMatches);
	if (best.length > 1) {
		return {
			...base,
			error: 'More than one column matches this association\'s account numbers equally — head the account # column "Account" so the import knows which one to use.',
		};
	}
	const accountCol = best[0].col;
	const isAccountRow = (r: number) => known.has(norm(at(r, accountCol)));

	// The percent column, ranked like the account column: a par-value / ownership header beats a
	// generic pct / % / share one, then the most values on account rows. A tie that remains is
	// refused — "Voting Share" and "Ownership %" can both total 100%, so the preview would not
	// catch the wrong pick (Codex P1, #703).
	const pctCandidates: Array<{ col: number; filled: number; header: string }> = [];
	for (let c = 0; c < width; c++) {
		const header = at(headerRow, c);
		if (c === accountCol || !PERCENT_HEADER.test(header)) continue;
		let filled = 0;
		for (let r = headerRow + 1; r < cells.length; r++) if (isAccountRow(r) && at(r, c) !== '') filled++;
		pctCandidates.push({ col: c, filled, header });
	}
	const pctPool = pctCandidates.filter((k) => OWNERSHIP_HEADER.test(k.header));
	const pctTier = pctPool.length > 0 ? pctPool : pctCandidates;
	const mostFilled = Math.max(...pctTier.map((k) => k.filled));
	const bestPct = pctTier.filter((k) => k.filled === mostFilled);
	if (bestPct.length > 1) {
		return {
			...base,
			error: `More than one ownership percent column (${bestPct.map((k) => `“${k.header}”`).join(', ')}) — keep only the one to import, or head it "Par Value".`,
		};
	}
	const pctCol = bestPct[0].col;

	// The scale is a property of the whole column: unmatched accounts count toward the ~1 total
	// too, or a few unknown units would flip every share to 100× too small (Codex P1, #703).
	// Rows with no account (a totals row) stay out.
	const bare: number[] = [];
	for (let r = headerRow + 1; r < cells.length; r++) {
		const acct = at(r, accountCol);
		const n = acct !== '' && !TOTAL_LABEL.test(acct) ? bareNumber(at(r, pctCol)) : null;
		if (n != null) bare.push(n);
	}
	const bareTotal = bare.reduce((s, n) => s + n, 0);
	const readAsFractions = bare.length > 0 && bare.every((n) => n > 0 && n <= 1) && bareTotal >= 0.95 && bareTotal <= 1.05;

	const result: ParValueSheetResult = {
		...base,
		accountColumn: at(headerRow, accountCol) || undefined,
		percentColumn: at(headerRow, pctCol),
		readAsFractions,
	};
	for (let r = headerRow + 1; r < cells.length; r++) {
		const accountNumber = at(r, accountCol);
		if (accountNumber === '' || TOTAL_LABEL.test(accountNumber)) continue;
		const value = at(r, pctCol);
		const isKnown = known.has(norm(accountNumber));
		const n = bareNumber(value);
		const bps = readAsFractions && n != null ? parsePercentToBps(n * 100) : parsePercentToBps(value);

		if (!isKnown) {
			if (bps != null) result.skipped.push({ accountNumber, reason: 'no account with this number' });
			continue;
		}
		if (value === '') {
			result.skipped.push({ accountNumber, reason: 'no ownership percent in this row' });
		} else if (bps == null) {
			result.skipped.push({ accountNumber, reason: `unreadable percent "${value}"` });
		} else {
			result.rows.push({ accountNumber, parValueBps: bps });
		}
	}
	return result;
};
